import { getContrastRatio, getContrastingButtonColors } from "./utils";

/**
 * Tema de colores de la interfaz: de las columnas de /v2/sergio/{tenant}/interfaz a variables CSS.
 * Toda variable de color sale SIEMPRE con valor: si la columna no llega, llega a null o no es un hex,
 * se deriva de las básicas (fondo, texto, foco, hover). Así el CSS no necesita respaldos y una
 * columna nueva funciona antes de que el backend la cree.
 */

const normalizeHex = (value) => {
  if (typeof value !== "string") return null;
  let h = value.trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.split("").map((c) => c + c).join("");
  if (/^[0-9a-f]{8}$/i.test(h)) h = h.slice(0, 6);
  return /^[0-9a-f]{6}$/i.test(h) ? `#${h.toLowerCase()}` : null;
};

const toRgb = (hex) => [0, 2, 4].map((i) => parseInt(hex.slice(1 + i, 3 + i), 16));

const rgbString = (hex) => toRgb(hex).join(", ");

// p = proporción de `fg` sobre `bg` (0-1)
const mix = (fg, bg, p) => {
  const a = toRgb(fg);
  const b = toRgb(bg);
  return `#${a.map((v, i) => Math.round(v * p + b[i] * (1 - p)).toString(16).padStart(2, "0")).join("")}`;
};

const contrast = (a, b) => getContrastRatio(a, b) ?? 0;

const readableOn = (bg) => (contrast("#000000", bg) >= contrast("#ffffff", bg) ? "#000000" : "#ffffff");

// Solo corrige datos imposibles (texto igual o casi igual que su fondo); no impone diseño.
const MIN_TEXT_GUARD = 1.5;
const ensureReadable = (text, bg) => (contrast(text, bg) >= MIN_TEXT_GUARD ? text : readableOn(bg));

const isLight = (bg) => contrast(bg, "#000000") > contrast(bg, "#ffffff");

export const buildThemeColors = (conf = {}) => {
  const c = (column) => normalizeHex(conf[column]);

  const background = c("backgroundcolor") ?? "#000000";
  const text = ensureReadable(c("textcolor") ?? readableOn(background), background);
  const surface = c("surfacecolor") ?? mix(text, background, 0.08);
  const border = c("bordercolor") ?? mix(text, background, 0.2);
  const textSecondary = c("textsecondarycolor") ?? mix(text, background, 0.65);
  const title = c("titlecolor") ?? text;

  const accent =
    c("accentcolor") ??
    [c("focuscolor"), c("hovercolor"), c("buttonfocusedbackgroundcolor"), c("titlecolor")].find(
      (color) => color && contrast(color, background) >= 2.5
    ) ??
    text;
  const hover = c("hovercolor") ?? accent;
  const focus = c("focuscolor") ?? accent;

  const defaultButton = c("buttondefaultbackgroundcolor") ?? background;
  const defaultButtonText = ensureReadable(c("buttondefaulttextcolor") ?? text, defaultButton);
  const hoverButton = c("buttonhoverbackgroundcolor") ?? hover;
  const focusedButton = c("buttonfocusedbackgroundcolor") ?? focus;
  const cta = getContrastingButtonColors({
    pageColor: background,
    buttonColor: defaultButton,
    buttonTextColor: defaultButtonText,
    accentColor: accent,
    pageTextColor: text,
  });

  const header = c("headerbackgroundcolor") ?? background;
  const submenu = c("submenubackgroundcolor") ?? c("discoverdropbgcolor") ?? surface;
  const submenuHover = c("submenuhovercolor") ?? hover;
  const submenuFocus = c("submenufocuscolor") ?? focus;
  const appColor = c("appcolor") ?? surface;
  const sidebar = c("sidebarcolor") ?? appColor;
  const footer = c("footerbackgroundcolor") ?? background;
  const featured = c("widgetdestacadobgcolor") ?? background;
  const arrow = c("arrowbackgroundcolor") ?? background;
  const tag = c("tagbackgroundcolor") ?? surface;
  const live = c("livebadgecolor") ?? "#ac0505";
  const error = c("errorcolor") ?? (isLight(background) ? "#c62828" : "#fd4c4c");
  const success = c("successcolor") ?? (isLight(background) ? "#1e7e34" : "#30d158");
  const rankingCell = c("rankingcellcolor") ?? surface;
  const rankingUserBanner = c("rankinguserbannercolor") ?? accent;
  const shadow = c("shadowcolor") ?? "#000000";

  return {
    "--backgroundColor": background,
    "--textColor": text,
    "--textSecondaryColor": textSecondary,
    "--titleColor": title,
    "--surfaceColor": surface,
    "--borderColor": border,
    "--shadowColor": shadow,
    "--appColor": appColor,

    "--accentColor": accent,
    "--accentTextColor": readableOn(accent),
    "--hoverColor": hover,
    "--hoverTextColor": readableOn(hover),
    "--focusColor": focus,
    "--focusTextColor": readableOn(focus),

    "--defaultButtonColor": defaultButton,
    "--defaultButtonTextColor": defaultButtonText,
    "--defaultButtonBorderColor": c("buttondefaultbordercolor") ?? text,
    "--hoverButtonColor": hoverButton,
    "--hoverButtonTextColor": ensureReadable(c("buttonhovertextcolor") ?? readableOn(hoverButton), hoverButton),
    "--hoverButtonBorderColor": c("buttonhoverbordercolor") ?? hoverButton,
    "--focusedButtonColor": focusedButton,
    "--focusedButtonTextColor": ensureReadable(c("buttonfocusedtextcolor") ?? readableOn(focusedButton), focusedButton),
    "--focusedButtonBorderColor": c("buttonfocusedbordercolor") ?? focusedButton,
    "--ctaButtonColor": cta.background,
    "--ctaButtonTextColor": cta.text,

    "--headerBackgroundColor": header,
    "--headerIconColor": ensureReadable(c("headericoncolor") ?? text, header),
    "--headerHoverIconColor": c("headerhovericoncolor") ?? hover,
    "--headerFocusIconColor": c("headerfocusiconcolor") ?? focus,
    "--menuSuperiorColor": c("menusuperiorcolor") ?? header,
    "--submenuBackgroundColor": submenu,
    "--submenuTextColor": ensureReadable(c("submenutextcolor") ?? text, submenu),
    "--submenuHoverColor": submenuHover,
    "--submenuHoverTextColor": readableOn(submenuHover),
    "--submenuFocusColor": submenuFocus,
    "--submenuFocusTextColor": readableOn(submenuFocus),
    "--sidebarColor": sidebar,
    "--sidebarIconColor": ensureReadable(c("sidebariconcolor") ?? text, sidebar),
    "--sidebarHoverIconColor": c("sidebarhovericoncolor") ?? hover,
    "--sidebarFocusIconColor": c("sidebarfocusiconcolor") ?? focus,
    "--footerBackgroundColor": footer,
    "--footerTextColor": contrast(text, footer) >= 3 ? text : readableOn(footer),

    "--rowTitleColor": c("rowtitlecolor") ?? c("widgetbannertitlecolor") ?? text,
    "--rowTextColor": c("rowtextcolor") ?? c("widgetbannertextcolor") ?? text,
    "--bannerWidgetBgColor": c("widgetbannerbgcolor") ?? "transparent",
    "--bannerWidgetImageColor": c("widgetbannerimagecolor") ?? "transparent",
    "--featuredWidgetBgColor": featured,
    "--featuredWidgetTitleColor": ensureReadable(c("widgetdestacadotitlecolor") ?? title, featured),
    "--featuredWidgetTextColor": ensureReadable(c("widgetdestacadotextcolor") ?? text, featured),
    "--arrowBackgroundColor": arrow,
    "--arrowIconColor": ensureReadable(c("arrowiconcolor") ?? text, arrow),
    "--contentPlayIconColor": c("contentplayiconcolor") ?? "#ffffff",
    "--contentPlayIconHoverColor": c("contentplayiconhovercolor") ?? hover,
    "--tagBackgroundColor": tag,
    "--tagTextColor": ensureReadable(c("tagtextcolor") ?? readableOn(tag), tag),
    "--liveBadgeColor": live,
    "--liveBadgeTextColor": readableOn(live),

    "--errorColor": error,
    "--errorTextColor": readableOn(error),
    "--successColor": success,
    "--successTextColor": readableOn(success),
    "--rankingCellColor": rankingCell,
    "--rankingCellTextColor": contrast(text, rankingCell) >= 4.5 ? text : readableOn(rankingCell),
    "--rankingUserBannerColor": rankingUserBanner,
    "--rankingUserBannerTextColor": contrast(text, rankingUserBanner) >= 4.5 ? text : readableOn(rankingUserBanner),

    "--catalogueHeadBackgroundColor": c("catalogueheadbackgroundcolor") ?? background,
    "--detailPageBgColor": c("detailpagebgcolor") ?? background,
    "--filterTitleColor": c("filtertitlecolor") ?? text,
    "--scrollbarThumbColor": c("scrollbarthumbcolor") ?? border,
    "--scrollbarTrackColor": c("scrollbartrackcolor") ?? background,

    "--textColorRgb": rgbString(text),
    "--backgroundColorRgb": rgbString(background),
    "--accentColorRgb": rgbString(accent),
    "--shadowColorRgb": rgbString(shadow),
    "--surfaceColorRgb": rgbString(surface),
  };
};

const buildThemeOthers = (conf = {}) => ({
  "--textFont": conf.font,
  "--titleFont": conf.titlefont,
  "--rowTitleFont": conf.rowtitlefont,
  "--rowTextFont": conf.rowtextfont,
  "--widgetTitleFontSize": conf.widgettitlefontsize || "2rem",
  "--bannerWidgetBorderRadius": conf.bannerborderradius != null ? `${conf.bannerborderradius}%` : null,
  "--backgroundImage": conf.backgroundimage,
  "--detailPageBgImage": conf.detailpagebgimage,
  "--loginBgImage": conf.loginbgimage,
  "--logo": conf.logo ? `url(${conf.logo})` : null,
});

/** Escribe el tema en <html>. Un valor vacío borra la variable (nunca se escribe "undefined"). */
export const applyTheme = (conf = {}, root = document.documentElement) => {
  const vars = { ...buildThemeColors(conf), ...buildThemeOthers(conf) };
  Object.entries(vars).forEach(([name, value]) => {
    if (value === null || value === undefined || value === "") root.style.removeProperty(name);
    else root.style.setProperty(name, String(value));
  });
};
